import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, icons, Units, useUnitsComponent } from "../../../Styles";
import { Activations, AddAnimalToPen, Button, ButtonList, FeedCurveViewer, PEN_BASIC, PEN_EDIT, ProgressCircle, StatusDisplay, StatusDisplayList } from "../../";
import { AttentionTypes, FeederStatus, penTypes } from "../../../Definitions/valueTypes";
import { _attentionColor, _feederColor } from "../../../Definitions/valueColors";
import { NavigationFunction as RootNavigation, Pages } from "../../../Navigation/Routers/root"
import { useNavigate } from "react-router"

import { getPenById } from "../../../Assets/TestData/lf_Data";
import { TestData as ComponentData } from '../../../Assets/TestData/componentsData';
import { TestData as FeedCurveData } from '../../../Assets/TestData/feedCurvesData';
import Last10DaysViewer, { TestData } from "../../curveViewer/animalGraph";
import { ButtonTypes } from "../../../Definitions";

interface penModal_Props {
  activePenId?: number
}
/**
 * PenModal
 * @param activePenId The pen number.
 * @returns A "div" to place in a modal to show all content from a Location Feeder Pen.
 */
export default function PenModal({activePenId}:penModal_Props) {
  const {t} = useTranslation();
  const navigator = useNavigate()
  const getUnit = useUnitsComponent();  

  const [showLastDays, setShowLastDays] = useState<boolean>(false); // Pen Modal Btn "Last 10 Days"
  const [showFeedPlan, setShowFeedPlan] = useState<boolean>(false); // Pen Modal Btn "Feed Plan"
  const [penEditMode, setPenEditMode] = useState<boolean>(false); // Pen Modal PenEdit Btn
  const [showAddAnimalToPenModal, setAddAnimalToPenModal] = useState(false) // Add Animal Modal
  
  // #region | Pen Modal Functionality
  // Pen Modal PenEdit Btn
  const switchPenEditMode = () => {
    setPenEditMode(!penEditMode)
  }

  // Pen Modal Add Animal To Pen Btn
  const handleEmptyPenBtn = () => {
    setAddAnimalToPenModal(true);
  }

  // Pen Modal Btn "Last 10 Days"
  const handleLastDaysClick = () => {
    setShowLastDays(true)
    setShowFeedPlan(false)
  }

  // Pen Modal Btn "Feed Plan"
  const handleFeedPlanClick = () => {
    setShowFeedPlan(true)
    setShowLastDays(false)
  }

  // Always set "setShowLastDays" to true and "setSwitchPenMode" to false when pen modal (activePenId) is "closed"
  useEffect(() => {
    if (!activePenId) {
      setShowLastDays(true);
    }
  }, [activePenId])
  // #endregion

  // #region | Pen Modal Data
  const activePen = activePenId ? getPenById(activePenId) : null;

  const animal = activePen
    ? activePen.animalId?.animalId
    : 0;

  const animalFoodTotal = activePenId
    ? activePen?.animalId?.totalFood
    : 0;

  const animalFoodProgressed = activePenId 
    ? activePen?.animalId?.remainingToday
    : 0;

  const feedComponent = ComponentData;

  const feedPlan = activePen
    ? activePen.animalId?.feedPlan
    : 0;

    
  const feedPlanData = useMemo(() => {
    return FeedCurveData.find((curve) => curve.id === feedPlan);
  }, [feedPlan]);

  const feeder = activePen
    ? activePen.feederStatus
    : "";

  const feederColor: FeederStatus = feeder ? feeder as FeederStatus : FeederStatus.off;

  const attention = activePen
    ? activePen.attention
    : "";

  const attentionColor: AttentionTypes = attention ? attention as AttentionTypes : AttentionTypes.neutral

  const hasActivator = activePen
    ? activePen.activator
    : false

  const motor = activePen
    ? activePen.motor
    : 0;
  // #endregion

  // #region | Pen Modal Components (Btns, Seperator, Overlay)
  // Btns for "Pen" and "PenEdit"
  const renderPenButtons = (Mode: 'isEditMode' | 'isShowMode') => {
    const content = Mode === 'isEditMode' 
      ? [t("buttons.saveBtn"), t("buttons.cancelBtn"), t("buttons.edit")] 
      : [t("buttons.edit")];
  
    const onClick = (i: number) => {
      if (Mode === 'isShowMode' && i === 0) {
        switchPenEditMode();
      } else if (Mode === 'isEditMode' && i === 1) {
        switchPenEditMode();
      }
    };
  
    const startEnhancers = Mode === 'isEditMode'
      ? [glyphs.Check({fillColor: colors.greyDarkPrimary}), glyphs.Close({fillColor: colors.greyDarkPrimary}), glyphs.Edit({fillColor: colors.greyDarkPrimary})]
      : [glyphs.Edit({fillColor: colors.greyDarkPrimary})];

    return (
      <ButtonList 
        contents={content}
        onClick={onClick}
        buttonType={ButtonTypes.tableButton}
        startEnhancers={startEnhancers}
        disabled={[false, false, true]}
        overrides={(index) => ({
          baseButton: {
            backgroundColor: index === 2 ? colors.greyLightSecondary : colors.greyLightPrimary
          },
          hoverStyles: {
            backgroundColor: colors.greyLightSecondary
          },
          disabledStyles: {
            opacity: index === 2 ? 1 : 0.5
          },
          contentStyles: {
            color: colors.greyDarkPrimary
          }
        })}
      />
    );
  };

  // Seperator Line
  const seperatorLine = <div style={{width: "90%", height: "1.5px", backgroundColor: colors.greyPrimary, margin: "20px auto"}}/>
  
  // Overlay
  const overlay = <div style={{ backgroundColor: colors.transparent, position: "absolute", top: 0, left: 0, bottom: 0, right: 0, zIndex: "999"}}/>
  // #endregion

  return (
    <div style={{backgroundColor: colors.greyLightTertiary, borderRadius: "0px 0px 10px 10px", padding: "30px 20px" }}>
      <div style={{ ...css.modalTop }}>
        {/* Activator, Feeder, Motor */}
        <StatusDisplayList 
          headers={[t("attentions.attention"), t("locationFeeder.penPopup.feeder"), t("locationFeeder.penPopup.motor")]} 
          values={["• " + attention ,"• " + feeder, motor + " mA"]}              
          // icons={[icons.attentionType, icons.feederLF, icons.motorLF]}
          icons={[glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"}), glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"}), glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"})]}
          valueColor={[ _attentionColor(attentionColor), _feederColor(feederColor), colors.greyDarkPrimary]}
        />
        {/* Fed Today */}
        <ProgressCircle header={t("feed.fed.today")}
        total={animalFoodTotal ?? 0} 
        progressed={animalFoodProgressed ?? 0} 
        unit={getUnit(Units.kG_grey)} 
        />
        {/* Fed During Period */}
        <ProgressCircle header={t("feed.fed.period")} 
        total={animalFoodTotal ?? 0} 
        progressed={animalFoodProgressed ?? 0}
        unit={getUnit(Units.kG_grey)}
        />
      </div>
      
      {seperatorLine}

      {/* Activations, Last Activation */}
      {hasActivator? <>
        <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "40px"}}>
          <StatusDisplay
            header={t("locationFeeder.penPopup.activator")} 
            value={"XXXXXX"}              
            // icon={icons.activatorLF}
            icon={glyphs.Attention({fillColor: colors.greyPrimary, size: "40px"})}
            valueColor={colors.greyDarkPrimary}
          />
          < Activations penId={activePenId ?? 0} /> 
        </div> 
        {seperatorLine}
      </> : <></>}

      <div style={{display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center"}}>
        {animal? <>
          {/* Animal in pen */}
          <div style={{display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "5px"}}>
            <img src={icons.animalsColor} style={{ width: "40px" }} alt="icon" />
            <div>
              <p style={{...fonts.header, ...fonts.fontSize4, color: colors.greyPrimary }}>{t("locationFeeder.penPopup.inThisPen")}</p>
              <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary }}>{t("animalPages.animal.animal")+" "+animal}</p>
            </div>
          </div>

          {/* Pen/PenEdit based on PenMode */}
          <div >
            <div style={{display: "flex", flexDirection: "row", padding: "0px 20px 0px 16px", justifyContent: penEditMode ? "end" : "space-between", alignItems: "end", zIndex: 10000, position: "relative"}}>
              {penEditMode? <></> : <p style={{ ...css.penHeaders }}>{t("locationFeeder.penPopup.penValueHeaders")}</p>}
              <div style={{display: "flex", flexDirection: "row", gap: "5px"}}>
              {penEditMode? <></> : 
                <Button 
                  content={t("locationFeeder.penPopup.goToAnimalPage")} 
                  buttonType={ButtonTypes.textButton}
                  onClick={() => {
                    RootNavigation(navigator, Pages.animals, Pages.animal, true, {selected:animal})
                  }}
                  overrides={{
                    baseButton: {
                      ...fonts.fontSize4,
                      padding: "0 10px",
                      height: "45px", 
                      backgroundColor: colors.greyLightPrimary, 
                      color: colors.greyDarkPrimary
                    },
                    hoverStyles: {
                      backgroundColor: colors.greyLightSecondary,
                      opacity: "1",
                    }
                  }}
                />
              }
              {penEditMode? renderPenButtons('isEditMode') : renderPenButtons('isShowMode')}
              </div>
            </div>
            <div style={{borderRadius: "10px", overflow: "hidden"}}>
              {penEditMode
                ? <><div style={{ zIndex: 10000, position: "relative" }}><PEN_EDIT penId={activePenId ?? 0} penType={penTypes.single}/></div>{overlay}</>
                : <PEN_BASIC penId={activePenId ?? 0} penType={penTypes.single}/>
              }
            </div>
          </div>

          {/* Animal btns (FeedPlan + Last 10 days) */}
          <div style={{margin: "10px 0px", display: "flex", justifyContent: "center"}}>
            <ButtonList contents={[t("locationFeeder.penPopup.lastDays"), t("feed.feedPlan.feedPlan")]} 
              overrides={{
                baseButton: {...css.button_FitContent}
              }}
              onClick={(i) => {
                if (i === 0) {
                  handleLastDaysClick()
                } else if (i === 1) {
                  handleFeedPlanClick()
                }
            }}/>
          </div>


                  
          <div>
            {/* PEN MODAL ANIMAL FEED PLAN */}
            { showFeedPlan? 
              <>      
                {/* FeedCurveViewer based on feedPlanData */}
                <div style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                  {feedPlanData ? (
                  <FeedCurveViewer data={feedPlanData} feedComponents={feedComponent} showComponentLabels={true} showMilestoneLabels={true}/>
                  ) : (
                  <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary,}}>No feed curve found</p> // Returner en tom div, hvis der ikke er nogen feedPlan
                  )}
                </div>
              </>         
            : // PEN MODAL ANIMAL LAST 10 DAYS
            <> 
              <div style={{marginTop: "20px"}}>
                <Last10DaysViewer data={TestData}/>
              </div> 
            </>
            }
          </div>
          </> : <>
            <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary, marginBottom: "10px" }}>{t("locationFeeder.penPopup.emptyPen")}</p>
            <Button
              content={t("locationFeeder.penPopup.addAnimalToPen")}
              onClick={handleEmptyPenBtn}
            />
            {/* Add Animal Modal */}
            <AddAnimalToPen isModalOpen={showAddAnimalToPenModal} activePen={activePenId ?? 0} goBack={() => setAddAnimalToPenModal(false)} />
          </>
        }
      </div>
    </div>
  )
}